import { IdentityMapper } from '..';
import { DocumentRightsResponseContainerDTO } from '../../../typings/windreamWebService/Windream.WebService';
import { IServiceResponse } from '../../ajaxHandler';
import { WindreamEntity } from '../../common';
import { GlobalConfig } from '../../config';
import { HttpResourcePointer, IRequestExecutor } from '../../dataProviders';
import { Logger } from '../../logging/';
import { DocumentRightsModel, IdentityRightsModelConverter } from '../../rights';
import { WindreamRequestOptions } from '../models';
import { ServiceAction } from '../serviceAction';

/**
 * Service action to get rights from an identity in the windream webservice.
 * 
 * @export
 * @class GetDocumentRights
 * @extends {ServiceAction}
 */
export class GetDocumentRights extends ServiceAction {
    private converter: IdentityRightsModelConverter;

    /**
     * Creates an instance of GetDocumentRights.
     * @param {IRequestExecutor} requestExecutor 
     * @param {GlobalConfig} globalConfig 
     * @param {Logger} logger 
     * @param {IdentityRightsModelConverter} converter 
     * @memberof GetDocumentRights
     */
    public constructor(requestExecutor: IRequestExecutor, globalConfig: GlobalConfig, logger: Logger, converter: IdentityRightsModelConverter) {
        super(requestExecutor, globalConfig, logger);
        this.converter = converter;

        this.name = 'getDocumentRights';
    }

    /**
     * Gets righs for the given document.
     * Resolves with the converted server response.
     * Overwrites `do()` method from parent class ServiceAction.
     *
     * @param {WindreamRequestOptions} windreamRequestOptions The request options.
     * @returns {Promise<DocumentRightsModel>} A promise, which will resolve with the converted server reponse.
     * @memberof GetDocumentRights
     */
    public async do(windreamRequestOptions: WindreamRequestOptions): Promise<DocumentRightsModel> {
        return new Promise<DocumentRightsModel>((resolve, reject) => {
            if (windreamRequestOptions.identity.entity !== WindreamEntity.Document) {
                this.logger.error('GetDocumentRights', 'do', 'Identity is not a document', windreamRequestOptions.identity);
                reject(new Error('Identity is not a document'));
                return;
            }
            const tempUrl = `${this.globalConfig.windreamWebServiceURL}/documents/GetDocumentRights?${IdentityMapper.getWebServiceGetParameter(windreamRequestOptions.identity)}`;
            this.requestExecutor.executeRequest(new HttpResourcePointer('GET', tempUrl, {
                Item: IdentityMapper.getWebServiceIdentity(windreamRequestOptions.identity)
            }), windreamRequestOptions.requestOptions).then((response: IServiceResponse<DocumentRightsResponseContainerDTO>) => {
                if (response && response.data && !response.data.HasErrors) {
                    resolve(this.converter.convertDocumentRightsToModel(response.data));
                } else {
                    this.logger.error('GetDocumentRights', 'do', 'Failed to get rights', response);
                    reject(new Error('Failed to get rights'));
                }
            }).catch((err: Error) => {
                this.logger.error('GetDocumentRights', 'do', 'Failed to execute request', err);
                reject(err);
            });
        });
    }
}